/**
 * @file: useConversationPeerPresence.ts
 *
 * Presence of the OTHER party in a private (1:1) conversation, for the
 * header subtitle. Returns `'online'`/`'offline'` for a private chat and
 * `null` for a group room (no single peer) or while members are loading.
 */

import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import {
  useIsOnline,
  usePresenceRefresh,
} from '@euler/app/flows/chat/lib/session/hooks/usePresence';
import { idEquals } from '@euler/utils';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useChatConversation } from '../context';
import { isGroupRoom } from '../handle';

export function useConversationPeerPresence(): 'online' | 'offline' | null {
  const { conversationHandle, members$ } = useChatConversation();
  const { me } = useChatSession();
  const [members] = useBehaviorSubject(members$);
  const peer = isGroupRoom(conversationHandle)
    ? null
    : members.find(
        m => m.type !== 'bot' && !idEquals(m.platformUserId, me.platformUserId),
      ) ?? null;
  // Seed the peer's presence (deep-link into a conversation the inbox never
  // listed); live events keep it current after.
  usePresenceRefresh(peer ? [peer] : []);
  const online = useIsOnline(peer);
  return peer ? (online ? 'online' : 'offline') : null;
}
